import { COUNTRIES } from '@revolugo/common/countries'
import { ContactPersonSalutationEnum } from '@revolugo/common/types'
import { isNil, omitBy } from '@revolugo/common/utils'

import type { CountryIso2Code } from '@revolugo/common/constants'
import type { PrebookFormValues } from '@revolugo/elements-vue3'
import type { LocationQueryValue } from 'vue-router'

function parseStringValue(
  value: LocationQueryValue | LocationQueryValue[] | null | undefined,
): string | undefined {
  if (!value) {
    return undefined
  }

  const stringValue = Array.isArray(value) ? value[0] : value
  return stringValue || undefined
}

function parseSalutation(
  salutation: LocationQueryValue | LocationQueryValue[] | null | undefined,
): ContactPersonSalutationEnum | undefined {
  if (!salutation) {
    return undefined
  }

  const stringValue = Array.isArray(salutation) ? salutation[0] : salutation
  if (!stringValue) {
    return undefined
  }

  // Valid salutation values
  const validSalutations = Object.values(ContactPersonSalutationEnum)

  if (validSalutations.includes(stringValue as ContactPersonSalutationEnum)) {
    return stringValue as ContactPersonSalutationEnum
  }

  return undefined
}

function parseNationality(
  nationality: LocationQueryValue | LocationQueryValue[] | null,
): CountryIso2Code | undefined {
  if (!nationality) {
    return undefined
  }

  const parsedNationality = (
    Array.isArray(nationality) ? nationality[0] : nationality
  )?.toLowerCase()

  const countries = Object.values(COUNTRIES)

  const iso2Match = countries.find(
    country => country.iso2.toLowerCase() === parsedNationality,
  )

  if (iso2Match) {
    return iso2Match.iso2 as CountryIso2Code
  }

  const nameMatch = countries.find(
    country =>
      country.name.toLowerCase() === parsedNationality ||
      country.name.split('(')[0]?.trim()?.toLowerCase() === parsedNationality,
  )

  if (nameMatch) {
    return nameMatch.iso2 as CountryIso2Code
  }

  return undefined
}

export const usePrebookQuery = (): {
  prebookProps: ComputedRef<Partial<PrebookFormValues>>
  updatePrebookQuery: (payload: PrebookFormValues) => Promise<void>
} => {
  const router = useRouter()
  const { query } = toRefs(useRoute())

  const parsedNationality = parseNationality(
    query.value.nationality || query.value.cNationality || null,
  )
  const parsedPhoneCountry = parseNationality(
    query.value.phoneCountry || query.value.cPhoneCountry || null,
  )

  const prebookProps = computed(() =>
    omitBy(
      {
        emailAddress: parseStringValue(query.value.email || query.value.cEmail),
        firstName: parseStringValue(
          query.value.firstName ||
            query.value.firstname ||
            query.value.cFirstname,
        ),
        lastName: parseStringValue(
          query.value.lastName || query.value.lastname || query.value.cLastname,
        ),
        nationality: parsedNationality,
        organizationAddress: parseStringValue(
          query.value.organizationAddress || query.value.oAddress,
        ),
        organizationCity: parseStringValue(
          query.value.organizationCity || query.value.oCity,
        ),
        organizationCountry: parseStringValue(
          query.value.organizationCountry || query.value.oCountry,
        ),
        organizationName: parseStringValue(
          query.value.organizationName || query.value.oName,
        ),
        organizationState: parseStringValue(
          query.value.organizationState || query.value.oState,
        ),
        organizationVatNumber: parseStringValue(
          query.value.organizationVatNumber || query.value.oVat,
        ),
        organizationZipCode: parseStringValue(
          query.value.organizationZipCode ||
            query.value.oZip ||
            query.value.oZipcode,
        ),
        phoneCountry: parsedPhoneCountry || parsedNationality,
        phoneNumber: parseStringValue(
          query.value.phoneNumber || query.value.cPhone,
        ),
        salutation: parseSalutation(
          query.value.salutation ||
            query.value.title ||
            query.value.cSalutation,
        ),
      },
      isNil,
    ),
  )

  async function updatePrebookQuery(payload: PrebookFormValues) {
    await router.replace({
      query: omitBy(
        {
          ...query.value,

          email: payload.emailAddress,
          firstName: payload.firstName,
          lastName: payload.lastName,
          nationality: payload.nationality,
          phoneCountry: payload.phoneCountry,
          phoneNumber: payload.phoneNumber,
          salutation: payload.salutation,
        },
        isNil,
      ) as Record<string, string>,
    })
  }

  return {
    prebookProps,
    updatePrebookQuery,
  }
}
